<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="container">
          <div class="hero-grid">
            <div class="hero-content">
              <h1 class="hero-title">Welcome to <span>{{ county?.countyName || 'County' }}</span> Self-Service Portal</h1>
              <p class="hero-subtitle">Access county services online - pay bills, apply for permits, and manage your accounts from anywhere, anytime.</p>
              <div class="hero-actions">
                <a href="/#/pay-bill" class="btn btn-primary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                    <line x1="2" y1="10" x2="22" y2="10"></line>
                  </svg>
                  Pay Bill Now
                </a>
                <a href="/#/services" class="btn btn-outline">
                  View All Services
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </a>
              </div>
            </div>
            <div class="hero-illustration">
              <div class="illustration-card">
                <div class="card-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"></path>
                  </svg>
                </div>
                <div class="card-stats">
                  <div class="stat-item">
                    <span class="stat-value">15+</span>
                    <span class="stat-label">Services</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-value">24/7</span>
                    <span class="stat-label">Access</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-value">100%</span>
                    <span class="stat-label">Online</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Quick Actions -->
      <section class="quick-actions-section">
        <div class="container">
          <div class="quick-actions-grid">
            <a href="/#/pay-bill" class="quick-action-card">
              <div class="action-icon blue">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                  <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
              </div>
              <h3>Pay Bill</h3>
              <p>Pay invoices via M-Pesa</p>
            </a>
            <a href="/#/print-receipt" class="quick-action-card">
              <div class="action-icon green">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
              </div>
              <h3>Print Receipt</h3>
              <p>Download payment receipts</p>
            </a>
            <a href="/#/business-registration" class="quick-action-card">
              <div class="action-icon purple">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"></path>
                </svg>
              </div>
              <h3>Business Permit</h3>
              <p>Register or renew business</p>
            </a>
            <a href="/#/contact" class="quick-action-card">
              <div class="action-icon orange">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
              </div>
              <h3>Get Help</h3>
              <p>Contact support team</p>
            </a>
          </div>
        </div>
      </section>

      <!-- Services Section -->
      <section class="services-section">
        <div class="container">
          <div class="section-header">
            <h2>Our Services</h2>
            <p>Access all county services from one place</p>
          </div>

          <div class="services-categories">
            <!-- Revenue & Payments -->
            <div class="category-group">
              <h3 class="category-title">
                <span class="category-icon blue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </span>
                Revenue & Payments
              </h3>
              <div class="services-grid">
                <a href="/#/pay-bill" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                      <line x1="2" y1="10" x2="22" y2="10"></line>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Pay Bill</h4>
                    <p>Pay invoices using M-Pesa paybill</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/print-receipt" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Print Receipt</h4>
                    <p>Download and print payment receipts</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
              </div>
            </div>

            <!-- Business Services -->
            <div class="category-group">
              <h3 class="category-title">
                <span class="category-icon purple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16"></path>
                    <path d="M9 7h1M9 11h1M14 7h1M14 11h1"></path>
                  </svg>
                </span>
                Business Services
              </h3>
              <div class="services-grid">
                <a href="/#/business-registration" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"></path>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Business Registration</h4>
                    <p>Register new business or renew permit</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/print-permit" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="6 9 6 2 18 2 18 9"></polyline>
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                      <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Print Business Permit</h4>
                    <p>Download your business permit certificate</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/health-certificates" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Health Certificates</h4>
                    <p>Apply for food hygiene certificates</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/fire-license" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Fire License</h4>
                    <p>Apply for fire safety clearance certificate</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
              </div>
            </div>

            <!-- Land & Property -->
            <div class="category-group">
              <h3 class="category-title">
                <span class="category-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </span>
                Land & Property
              </h3>
              <div class="services-grid">
                <a href="/#/land-rates" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Land Rates</h4>
                    <p>Pay land rates and property charges</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/house-rents" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>House Rents</h4>
                    <p>Pay county house rental fees</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/building-permits" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
                      <rect x="9" y="9" width="6" height="6"></rect>
                      <line x1="9" y1="1" x2="9" y2="4"></line>
                      <line x1="15" y1="1" x2="15" y2="4"></line>
                      <line x1="9" y1="20" x2="9" y2="23"></line>
                      <line x1="15" y1="20" x2="15" y2="23"></line>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Building Permits</h4>
                    <p>Apply for construction approvals</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/property-self-service" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Property Self Service</h4>
                    <p>View property details and balances</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
              </div>
            </div>

            <!-- Utilities & Environment -->
            <div class="category-group">
              <h3 class="category-title">
                <span class="category-icon cyan">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                  </svg>
                </span>
                Utilities & Environment
              </h3>
              <div class="services-grid">
                <a href="/#/water-services" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Water Services</h4>
                    <p>Water connections and bill payments</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
                <a href="/#/waste-management" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Waste Management</h4>
                    <p>Refuse collection and disposal services</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
              </div>
            </div>

            <!-- Parking & Transport -->
            <div class="category-group">
              <h3 class="category-title">
                <span class="category-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </span>
                Parking & Transport
              </h3>
              <div class="services-grid">
                <a href="/#/daily-parking" class="service-card">
                  <div class="service-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="1" y="3" width="22" height="18" rx="2" ry="2"></rect>
                      <path d="M9 14V8h3a2 2 0 1 1 0 4H9"></path>
                    </svg>
                  </div>
                  <div class="service-info">
                    <h4>Daily Parking</h4>
                    <p>Pay for daily parking fees</p>
                  </div>
                  <span class="arrow-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- How It Works Section -->
      <section class="how-it-works-section">
        <div class="container">
          <div class="section-header centered">
            <h2>How It Works</h2>
            <p>Simple steps to access county services online</p>
          </div>

          <div class="steps-grid">
            <div class="step-card">
              <div class="step-number">1</div>
              <div class="step-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
              <h3>Select Service</h3>
              <p>Browse and choose the county service you need from our catalog</p>
            </div>
            <div class="step-card">
              <div class="step-number">2</div>
              <div class="step-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </div>
              <h3>Fill Details</h3>
              <p>Complete the application form with required information</p>
            </div>
            <div class="step-card">
              <div class="step-number">3</div>
              <div class="step-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                  <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
              </div>
              <h3>Make Payment</h3>
              <p>Pay securely via M-Pesa using the invoice number</p>
            </div>
            <div class="step-card">
              <div class="step-number">4</div>
              <div class="step-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3>Get Receipt</h3>
              <p>Download your receipt and any applicable documents</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Payment Methods -->
      <section class="payment-section">
        <div class="container">
          <div class="payment-card">
            <div class="payment-content">
              <h2>Easy Payment Options</h2>
              <p>Pay for all county services using M-Pesa. Simply use the paybill number and your invoice number as the account.</p>
              <div class="paybill-info" v-if="county">
                <div class="paybill-box">
                  <span class="paybill-label">Paybill Number</span>
                  <strong class="paybill-number">{{ county.paybillNumber || '123456' }}</strong>
                </div>
                <div class="paybill-box">
                  <span class="paybill-label">Account Number</span>
                  <strong class="paybill-number">Invoice No.</strong>
                </div>
              </div>
            </div>
            <div class="payment-icon">
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section class="contact-section">
        <div class="container">
          <div class="contact-grid">
            <div class="contact-info">
              <h2>Need Assistance?</h2>
              <p>Our support team is here to help you with any questions about county services.</p>
              <div class="contact-methods">
                <div class="contact-item">
                  <div class="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                  <div>
                    <strong>Phone</strong>
                    <span>{{ county?.telephone || '0700 000 000' }}</span>
                  </div>
                </div>
                <div class="contact-item">
                  <div class="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <div>
                    <strong>Email</strong>
                    <span>{{ county?.email || 'info@county.go.ke' }}</span>
                  </div>
                </div>
              </div>
              <a href="/#/contact" class="btn btn-primary">
                Contact Us
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </a>
            </div>
            <div class="office-hours">
              <h3>Office Hours</h3>
              <ul>
                <li><span>Monday - Friday</span><strong>8:00 AM - 5:00 PM</strong></li>
                <li><span>Saturday</span><strong>9:00 AM - 1:00 PM</strong></li>
                <li><span>Sunday & Holidays</span><strong>Closed</strong></li>
              </ul>
              <div class="online-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Online services available 24/7
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { biller, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: 'OverviewPage',
  components: { HeaderComponent, FooterComponent },

  data() {
    return {
      county: null
    };
  },

  mounted() {
    this.getCounty();
  },

  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (e) { console.error(e); }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.page-wrapper { font-family: 'Plus Jakarta Sans', sans-serif; background: #f5f7fa; min-height: 100vh; }
.main-content { }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Hero Section */
.hero-section { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 50%, #0a3459 100%); padding: 80px 0; position: relative; overflow: hidden; }
.hero-section::before { content: ''; position: absolute; top: 0; right: 0; width: 600px; height: 600px; background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%); transform: translate(20%, -20%); }
.hero-section::after { content: ''; position: absolute; bottom: 0; left: 0; width: 400px; height: 400px; background: radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%); transform: translate(-30%, 30%); }

.hero-grid { display: grid; grid-template-columns: 1fr 400px; gap: 60px; align-items: center; position: relative; z-index: 1; }
.hero-content { }
.hero-title { font-size: 48px; font-weight: 800; color: white; margin: 0 0 20px; line-height: 1.1; letter-spacing: -1px; }
.hero-title span { color: #7dd3fc; }
.hero-subtitle { font-size: 18px; color: rgba(255,255,255,0.8); margin: 0 0 32px; line-height: 1.6; max-width: 500px; }
.hero-actions { display: flex; gap: 16px; }

.illustration-card { background: rgba(255,255,255,0.1); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.2); border-radius: 24px; padding: 40px; text-align: center; }
.card-icon { width: 100px; height: 100px; background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%); border-radius: 24px; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; color: white; }
.card-stats { display: flex; justify-content: center; gap: 32px; }
.stat-item { text-align: center; }
.stat-value { display: block; font-size: 28px; font-weight: 800; color: white; }
.stat-label { font-size: 13px; color: rgba(255,255,255,0.7); }

/* Buttons */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 16px 28px; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.3s; border: none; font-family: inherit; text-decoration: none; }
.btn-primary { background: linear-gradient(135deg, #059669 0%, #047857 100%); color: white; box-shadow: 0 4px 15px rgba(5, 150, 105, 0.3); }
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4); }
.btn-outline { background: rgba(255,255,255,0.1); color: white; border: 2px solid rgba(255,255,255,0.3); }
.btn-outline:hover { background: rgba(255,255,255,0.2); border-color: rgba(255,255,255,0.5); }

/* Quick Actions */
.quick-actions-section { margin-top: -40px; position: relative; z-index: 10; padding-bottom: 60px; }
.quick-actions-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.quick-action-card { background: white; border-radius: 16px; padding: 28px; text-align: center; text-decoration: none; box-shadow: 0 4px 20px rgba(0,0,0,0.08); transition: all 0.3s; }
.quick-action-card:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(0,0,0,0.12); }
.action-icon { width: 64px; height: 64px; border-radius: 16px; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
.action-icon.blue { background: #dbeafe; color: #2563eb; }
.action-icon.green { background: #d1fae5; color: #059669; }
.action-icon.purple { background: #ede9fe; color: #7c3aed; }
.action-icon.orange { background: #ffedd5; color: #ea580c; }
.quick-action-card h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 6px; }
.quick-action-card p { font-size: 13px; color: #64748b; margin: 0; }

/* Services Section */
.services-section { padding: 60px 0; }
.section-header { margin-bottom: 40px; }
.section-header.centered { text-align: center; }
.section-header h2 { font-size: 32px; font-weight: 800; color: #1e293b; margin: 0 0 12px; }
.section-header p { font-size: 16px; color: #64748b; margin: 0; }

.services-categories { display: flex; flex-direction: column; gap: 40px; }
.category-group { }
.category-title { display: flex; align-items: center; gap: 12px; font-size: 18px; font-weight: 700; color: #1e293b; margin: 0 0 20px; }
.category-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
.category-icon.blue { background: #dbeafe; color: #2563eb; }
.category-icon.purple { background: #ede9fe; color: #7c3aed; }
.category-icon.green { background: #d1fae5; color: #059669; }
.category-icon.cyan { background: #cffafe; color: #0891b2; }
.category-icon.orange { background: #ffedd5; color: #ea580c; }

.services-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
.service-card { display: flex; align-items: center; gap: 16px; padding: 20px; background: white; border: 1px solid #e2e8f0; border-radius: 14px; text-decoration: none; transition: all 0.2s; }
.service-card:hover { border-color: #0f4c81; box-shadow: 0 4px 15px rgba(15, 76, 129, 0.1); }
.service-card:hover .arrow-icon { transform: translateX(4px); color: #0f4c81; }
.service-icon { width: 48px; height: 48px; background: #f1f5f9; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #475569; flex-shrink: 0; }
.service-info { flex: 1; }
.service-info h4 { font-size: 15px; font-weight: 700; color: #1e293b; margin: 0 0 4px; display: flex; align-items: center; gap: 8px; }
.service-info p { font-size: 13px; color: #64748b; margin: 0; }
.arrow-icon { color: #94a3b8; transition: all 0.2s; }

/* How It Works */
.how-it-works-section { padding: 60px 0; background: white; }
.steps-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
.step-card { text-align: center; padding: 32px 24px; position: relative; }
.step-number { position: absolute; top: 24px; left: 24px; width: 28px; height: 28px; background: #0f4c81; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; }
.step-icon { width: 80px; height: 80px; background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border-radius: 20px; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; color: #0369a1; }
.step-card h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
.step-card p { font-size: 13px; color: #64748b; margin: 0; line-height: 1.5; }

/* Payment Section */
.payment-section { padding: 60px 0; }
.payment-card { background: linear-gradient(135deg, #059669 0%, #047857 100%); border-radius: 24px; padding: 48px; display: flex; align-items: center; justify-content: space-between; position: relative; overflow: hidden; }
.payment-card::before { content: ''; position: absolute; top: -50px; right: -50px; width: 200px; height: 200px; background: rgba(255,255,255,0.1); border-radius: 50%; }
.payment-content { position: relative; z-index: 1; max-width: 500px; }
.payment-content h2 { font-size: 28px; font-weight: 800; color: white; margin: 0 0 12px; }
.payment-content p { font-size: 15px; color: rgba(255,255,255,0.9); margin: 0 0 24px; line-height: 1.6; }
.paybill-info { display: flex; gap: 20px; }
.paybill-box { background: rgba(255,255,255,0.15); border-radius: 12px; padding: 16px 24px; }
.paybill-label { display: block; font-size: 12px; color: rgba(255,255,255,0.8); margin-bottom: 4px; }
.paybill-number { display: block; font-size: 24px; font-weight: 800; color: white; }
.payment-icon { color: rgba(255,255,255,0.2); position: relative; z-index: 1; }

/* Contact Section */
.contact-section { padding: 60px 0 80px; }
.contact-grid { display: grid; grid-template-columns: 1fr 360px; gap: 48px; background: white; border-radius: 24px; padding: 48px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
.contact-info h2 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0 0 12px; }
.contact-info > p { font-size: 15px; color: #64748b; margin: 0 0 24px; }
.contact-methods { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
.contact-item { display: flex; align-items: center; gap: 16px; }
.contact-icon { width: 48px; height: 48px; background: #f1f5f9; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.contact-item strong { display: block; font-size: 14px; font-weight: 600; color: #1e293b; }
.contact-item span { font-size: 14px; color: #64748b; }

.office-hours { background: #f8fafc; border-radius: 16px; padding: 28px; }
.office-hours h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 20px; }
.office-hours ul { list-style: none; padding: 0; margin: 0 0 20px; }
.office-hours li { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #e2e8f0; }
.office-hours li:last-child { border-bottom: none; }
.office-hours li span { font-size: 14px; color: #64748b; }
.office-hours li strong { font-size: 14px; font-weight: 600; color: #1e293b; }
.online-badge { display: flex; align-items: center; gap: 10px; padding: 14px 18px; background: #d1fae5; border-radius: 10px; font-size: 13px; font-weight: 600; color: #059669; }

/* Responsive */
@media (max-width: 1024px) {
  .hero-grid { grid-template-columns: 1fr; text-align: center; }
  .hero-subtitle { margin-left: auto; margin-right: auto; }
  .hero-actions { justify-content: center; }
  .illustration-card { display: none; }
  .quick-actions-grid { grid-template-columns: repeat(2, 1fr); }
  .steps-grid { grid-template-columns: repeat(2, 1fr); }
  .contact-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .hero-section { padding: 60px 0; }
  .hero-title { font-size: 32px; }
  .quick-actions-grid { grid-template-columns: 1fr; }
  .services-grid { grid-template-columns: 1fr; }
  .steps-grid { grid-template-columns: 1fr; }
  .payment-card { flex-direction: column; text-align: center; padding: 32px; }
  .payment-icon { display: none; }
  .paybill-info { flex-direction: column; }
  .hero-actions { flex-direction: column; }
}
</style>